/**
 * Report Doc Builder
 *
 * Turns the facts from computeSummaryReport (reportMetrics.js) into a Docs page
 * (TipTap JSON). Pure: text comes from the injected t() so the same builder
 * serves both languages and can be tested without the app.
 *
 * Every number written here comes from the report facts; this module does no
 * counting of its own.
 *
 * @module services/reportDoc
 */

import { PACE_TOLERANCE, REPORT_PRIORITIES, REPORT_STATUSES } from './reportMetrics.js';

const STATE_TONES = {
    late: 'red',
    behind: 'amber',
    onTrack: 'blue',
    ahead: 'green',
    notStarted: 'grey',
    noDates: 'grey',
    noTasks: 'grey',
    done: 'green'
};

const STATUS_TONES = {
    done: 'green',
    progress: 'blue',
    review: 'purple',
    todo: 'amber',
    backlog: 'grey'
};

const PRIORITY_TONES = { high: 'red', medium: 'amber', low: 'green' };

// Deep links into the app for whatever a section leaves out. They use the routes the
// app already understands (task/project ID filters) and open in a new tab. Hidden in print.
export function makeReportLinks(baseUrl = '') {
    const base = String(baseUrl || '');
    return {
        tasks: ids => (ids && ids.length ? `${base}#tasks?view=list&taskIds=${ids.join(',')}&taskSrc=report` : ''),
        projects: ids => (ids && ids.length ? `${base}#projects?projectIds=${ids.join(',')}&projectSrc=report` : ''),
        // Single entities: the task detail / project page the app already routes to.
        task: id => (id != null && id !== '' ? `${base}#task-${id}` : ''),
        project: id => (id != null && id !== '' ? `${base}#project-${id}` : ''),
        // A group of same-title tasks opens as a list; a single one opens its detail.
        taskGroup(ids) { return ids && ids.length > 1 ? this.tasks(ids) : this.task(ids && ids[0]); }
    };
}

// ── TipTap node helpers ─────────────────────────────────────────────────
const text = (value, marks) => {
    const str = String(value ?? '');
    if (!str) return null;
    return marks ? { type: 'text', text: str, marks } : { type: 'text', text: str };
};
const bold = value => text(value, [{ type: 'bold' }]);
const link = (value, href) => (href ? text(`${value} ↗`, [{ type: 'link', attrs: { href, target: '_blank', rel: 'noopener' } }]) : null);
// A task/project name that opens it in Nautilus (falls back to plain text without an href).
const anchor = (value, href, isBold = false) => {
    const marks = [];
    if (isBold) marks.push({ type: 'bold' });
    if (href) marks.push({ type: 'link', attrs: { href, target: '_blank', rel: 'noopener' } });
    return text(value, marks.length ? marks : undefined);
};
// Text that follows a name inside a translated sentence ("{name} starts · 19 tasks").
const afterName = (sentence, name) => (sentence.startsWith(name) ? sentence.slice(name.length) : ` · ${sentence}`);
const chip = (tone, label) => ({ type: 'statusChip', attrs: { tone, label: String(label) } });
const dateChip = date => ({ type: 'dateChip', attrs: { date } });
const paragraph = (...inline) => {
    const content = inline.flat().filter(Boolean);
    return content.length ? { type: 'paragraph', content } : { type: 'paragraph' };
};
const heading = (level, value) => ({ type: 'heading', attrs: { level }, content: [text(value)] });
const callout = (tone, ...blocks) => ({ type: 'callout', attrs: { tone, icon: true }, content: blocks.flat() });
const bulletList = items => ({
    type: 'bulletList',
    content: items.map(inline => ({ type: 'listItem', content: [paragraph(inline)] }))
});
const cell = (type, inline) => ({
    type,
    attrs: { colspan: 1, rowspan: 1, colwidth: null },
    content: [paragraph(inline)]
});
const table = (headers, rows) => ({
    type: 'table',
    content: [
        { type: 'tableRow', content: headers.map(h => cell('tableHeader', [text(h)])) },
        ...rows.map(row => ({
            type: 'tableRow',
            content: row.map(value => cell('tableCell', Array.isArray(value) ? value : [text(value)]))
        }))
    ]
});
const columns = (left, right) => ({
    type: 'docColumns',
    content: [
        { type: 'docColumn', content: left.length ? left : [paragraph()] },
        { type: 'docColumn', content: right.length ? right : [paragraph()] }
    ]
});

// ── Formatting ──────────────────────────────────────────────────────────
export function makeFormatters(locale) {
    const dayFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    const monthFmt = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' });
    const numFmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
    return {
        day: iso => dayFmt.format(new Date(`${iso}T00:00:00Z`)),
        month: key => monthFmt.format(new Date(`${key}-01T00:00:00Z`)),
        num: n => numFmt.format(n)
    };
}

// ── Sections ────────────────────────────────────────────────────────────
function kpiSection(r, t, f) {
    const k = r.kpis;
    const none = t('report.summary.common.none');
    const rows = [
        [t('report.summary.kpis.tasks'), [bold(k.totalTasks)], t('report.summary.kpis.tasksDetail', { count: k.createdThisMonth })],
        [t('report.summary.kpis.completed'), [bold(t('report.summary.kpis.countPct', { count: k.counts.done, pct: k.donePct }))], t('report.summary.kpis.completedDetail', { count: k.completedThisMonth })],
        [t('report.summary.kpis.inProgress'), [bold(k.counts.progress + k.counts.review)], t('report.summary.kpis.inProgressDetail', { count: k.projectsInProgress })],
        [t('report.summary.kpis.overdue'), [bold(k.overdue)], t('report.summary.kpis.overdueDetail', { pct: k.overduePctOfOpen })],
        [t('report.summary.kpis.lateProjects'), [bold(k.lateProjects)], t('report.summary.kpis.lateProjectsDetail')],
        k.nextMilestone
            ? [t('report.summary.kpis.nextMilestone'), [bold(t('report.summary.kpis.daysLeft', { days: k.nextMilestone.daysLeft }))], [text(`${k.nextMilestone.name} · `), dateChip(k.nextMilestone.endDate)]]
            : [t('report.summary.kpis.nextMilestone'), [text(none)], t('report.summary.kpis.noMilestone')]
    ];
    return [
        heading(2, t('report.summary.kpis.heading')),
        table([t('report.summary.kpis.colMetric'), t('report.summary.kpis.colValue'), t('report.summary.kpis.colDetail')], rows)
    ];
}

/**
 * Highlight sentences shared by the Docs page and the report view, so both say
 * exactly the same thing. Tone is one of danger / warning / info / success.
 * @returns {Array<{tone: string, text: string}>}
 */
export function buildHighlightItems(r, t, f) {
    const items = [];
    const push = (tone, value) => items.push({ tone, text: value });
    const late = r.projects.filter(p => p.state === 'late');
    const behind = r.projects.filter(p => p.state === 'behind');
    // At most 3 names, then "and N more" — highlights must stay one line at any scale.
    const names = list => list.length > 3
        ? t('report.summary.common.andMore', { names: list.slice(0, 3).map(p => p.name).join(', '), count: list.length - 3 })
        : list.map(p => p.name).join(', ');

    if (late.length === 1) {
        const p = late[0];
        push('danger', t('report.summary.highlights.lateOne', { name: p.name, days: p.daysOverdue, open: p.remaining }));
    } else if (late.length > 1) {
        const open = late.reduce((s, p) => s + p.remaining, 0);
        push('danger', t('report.summary.highlights.lateMany', { count: late.length, open, names: names(late) }));
    }
    if (behind.length === 1) {
        const p = behind[0];
        push('warning', t('report.summary.highlights.behindOne', { name: p.name, time: p.timePct, done: p.donePct }));
    } else if (behind.length > 1) {
        push('warning', t('report.summary.highlights.behindMany', { count: behind.length, names: names(behind) }));
    }
    if (r.kpis.overdue > 0) {
        const oldest = r.attention.rows.length ? r.attention.rows[0].maxDaysOverdue : 0;
        push('warning', t('report.summary.highlights.overdue', { count: r.kpis.overdue, days: oldest }));
    }
    const starts = r.upcoming.rows.filter(u => u.kind === 'projectStart');
    if (starts.length === 1) {
        push('info', t('report.summary.highlights.startOne', { name: starts[0].name, date: f.day(starts[0].date), tasks: starts[0].count }));
    } else if (starts.length > 1) {
        const tasks = starts.reduce((s, u) => s + u.count, 0);
        push('info', t('report.summary.highlights.startMany', { count: starts.length, days: r.upcoming.days, tasks }));
    }
    const m = r.kpis.nextMilestone;
    if (m) {
        push('info', t('report.summary.highlights.milestone', { name: m.name, date: f.day(m.endDate), days: m.daysLeft }));
    }
    if (!late.length && !behind.length && r.kpis.overdue === 0) {
        items.unshift({ tone: 'success', text: t('report.summary.highlights.allClear') });
    }
    return items;
}

function highlightsSection(r, t, f) {
    return [
        heading(2, t('report.summary.highlights.heading')),
        ...buildHighlightItems(r, t, f).map(item => callout(item.tone, paragraph(text(item.text))))
    ];
}

function projectsSection(r, t, f, links) {
    const none = t('report.summary.common.none');
    const rows = r.projects.map(p => [
        [anchor(p.name, links.project(p.id), true)],
        [chip(STATE_TONES[p.state], t(`report.summary.state.${p.state}`))],
        p.total ? t('report.summary.projects.doneOf', { done: p.counts.done, total: p.total }) : none,
        p.total ? `${p.donePct}%` : none,
        p.timePct === null ? none : `${p.timePct}%`,
        p.endDate
            ? [dateChip(p.endDate), p.daysOverdue ? text(` ${t('report.summary.projects.lateBy', { days: p.daysOverdue })}`) : null]
            : none
    ]);
    const extra = (label, group) => {
        if (!group.total) return;
        rows.push([[text(label)], none, t('report.summary.projects.doneOf', { done: group.counts.done, total: group.total }), `${Math.round((group.counts.done / group.total) * 100)}%`, none, none]);
    };
    extra(t('report.summary.projects.unassigned'), r.unassigned);
    extra(t('report.summary.projects.archived'), r.archivedProjectTasks);

    if (!rows.length) {
        return [heading(2, t('report.summary.projects.heading')), paragraph(text(t('report.summary.projects.empty')))];
    }
    return [
        heading(2, t('report.summary.projects.heading')),
        paragraph(text(t('report.summary.projects.intro'))),
        table([
            t('report.summary.projects.colProject'),
            t('report.summary.projects.colState'),
            t('report.summary.projects.colCompleted'),
            t('report.summary.projects.colProgress'),
            t('report.summary.projects.colTime'),
            t('report.summary.projects.colEnd')
        ], rows)
    ];
}

function throughputSection(r, t, f) {
    const tp = r.throughput;
    if (!tp.hasHistory) return [];
    const last = tp.months[tp.months.length - 1];
    const rows = tp.months.map(m => [
        f.month(m.month),
        String(m.created),
        String(m.completed),
        String(m.cumCreated - m.cumCompleted)
    ]);
    const summary = tp.avgCompletedPerMonth > 0
        ? t('report.summary.throughput.pace', { avg: f.num(tp.avgCompletedPerMonth), remaining: tp.remaining, months: tp.monthsToClear })
        : t('report.summary.throughput.noPace');
    return [
        heading(2, t('report.summary.throughput.heading')),
        paragraph(text(t('report.summary.throughput.intro', { month: f.month(last.month), date: f.day(r.cutDate) }))),
        table([
            t('report.summary.throughput.colMonth'),
            t('report.summary.throughput.colCreated'),
            t('report.summary.throughput.colCompleted'),
            t('report.summary.throughput.colOpen')
        ], rows),
        paragraph(text(summary))
    ];
}

function deadlinesSection(r, t, f, links) {
    const left = [heading(3, t('report.summary.deadlines.agingHeading'))];
    if (r.kpis.overdue === 0) {
        left.push(paragraph(text(t('report.summary.deadlines.noOverdue'))));
    } else {
        left.push(table(
            [t('report.summary.deadlines.colDelay'), t('report.summary.deadlines.colTasks')],
            r.aging.map(b => [t(`report.summary.deadlines.bucket.${b.key}`), [anchor(String(b.count), b.count ? links.tasks(b.taskIds) : '')]])
        ));
    }

    const right = [heading(3, t('report.summary.deadlines.upcomingHeading', { days: r.upcoming.days }))];
    if (!r.upcoming.rows.length) {
        right.push(paragraph(text(t('report.summary.deadlines.upcomingEmpty'))));
    } else {
        right.push(bulletList(r.upcoming.rows.map(u => {
            if (u.kind === 'projectStart' || u.kind === 'projectEnd') {
                const key = u.kind === 'projectStart' ? 'report.summary.deadlines.projectStart' : 'report.summary.deadlines.projectEnd';
                return [dateChip(u.date), text(' '), anchor(u.name, links.project(u.projectId)), text(afterName(t(key, { name: u.name, count: u.count }), u.name))];
            }
            return [
                dateChip(u.date), text(' '),
                anchor(u.name, links.taskGroup(u.taskIds)),
                u.count > 1 ? text(` ×${u.count}`) : null,
                u.projectName ? text(' · ') : null,
                u.projectName ? anchor(u.projectName, links.project(u.projectId)) : null
            ];
        })));
        if (r.upcoming.total > r.upcoming.rows.length) {
            const href = links.tasks(r.upcoming.hiddenTaskIds);
            right.push(paragraph(text(t('report.summary.deadlines.more', { count: r.upcoming.total - r.upcoming.rows.length })), href ? text(' ') : null, link(t('report.view.link.tasks'), href)));
        }
    }
    return [heading(2, t('report.summary.deadlines.heading')), columns(left, right)];
}

function attentionSection(r, t, f, links) {
    const head = [heading(2, t('report.summary.attention.heading'))];
    if (!r.attention.rows.length) {
        return [...head, paragraph(text(t('report.summary.attention.empty')))];
    }
    const none = t('report.summary.common.none');
    const rows = r.attention.rows.map(g => {
        const statuses = REPORT_STATUSES
            .map(s => [s, g.statusCounts[s]])
            .filter(([, n]) => n > 0)
            .map(([s, n]) => chip(STATUS_TONES[s], g.count > 1 ? `${t(`tasks.status.${s}`)} ${n}` : t(`tasks.status.${s}`)));
        const priorities = REPORT_PRIORITIES
            .filter(priority => g.priorityCounts?.[priority] > 0)
            .map(priority => chip(PRIORITY_TONES[priority], g.priorityCounts[priority] > 1 ? `${t(`tasks.priority.${priority}`)} ${g.priorityCounts[priority]}` : t(`tasks.priority.${priority}`)));
        return [
            [anchor(g.title, links.taskGroup(g.taskIds), true), g.count > 1 ? text(` ×${g.count}`) : null],
            g.projectName ? [anchor(g.projectName, links.project(g.projectId))] : t('report.summary.projects.unassigned'),
            statuses.flatMap((c, i) => (i ? [text(' '), c] : [c])),
            priorities.length ? priorities.flatMap((chipNode, i) => (i ? [text(' '), chipNode] : [chipNode])) : none,
            t('report.summary.attention.days', { days: g.maxDaysOverdue })
        ];
    });
    const blocks = [
        ...head,
        paragraph(text(t('report.summary.attention.intro'))),
        table([
            t('report.summary.attention.colTask'),
            t('report.summary.attention.colProject'),
            t('report.summary.attention.colStatus'),
            t('report.summary.attention.colPriority'),
            t('report.summary.attention.colOverdue')
        ], rows)
    ];
    if (r.attention.totalGroups > r.attention.rows.length) {
        const href = links.tasks(r.attention.hiddenTaskIds);
        blocks.push(paragraph(text(t('report.summary.attention.more', { shown: r.attention.rows.length, total: r.attention.totalGroups })), href ? text(' ') : null, link(t('report.view.link.tasks'), href)));
    }
    return blocks;
}

function methodSection(r, t, f) {
    const q = r.dataQuality;
    const items = [
        ['projectsWithoutTasks', q.projectsWithoutTasks],
        ['projectsWithoutDates', q.projectsWithoutDates],
        ['tasksWithoutProject', q.tasksWithoutProject],
        ['tasksInArchivedProjects', q.tasksInArchivedProjects],
        ['doneWithoutCompletedDate', q.doneWithoutCompletedDate]
    ].filter(([, n]) => n > 0).map(([key, n]) => [text(`${t(`report.summary.quality.${key}`)}: `), bold(n)]);
    if (!r.throughput.hasHistory) items.push([text(t('report.summary.quality.throughputHidden'))]);

    return [
        heading(2, t('report.summary.method.heading')),
        callout('neutral',
            paragraph(text(t('report.summary.method.body', { date: f.day(r.cutDate), tolerance: PACE_TOLERANCE }))),
            paragraph(bold(t('report.summary.quality.heading'))),
            items.length ? bulletList(items) : paragraph(text(t('report.summary.quality.none')))
        )
    ];
}

/**
 * Build the Docs page for a summary report.
 *
 * @param {Object} report - Output of computeSummaryReport
 * @param {Object} options
 * @param {Function} options.t - i18n lookup t(key, vars)
 * @param {string} [options.locale='en-US'] - Locale for dates and numbers
 * @param {string} [options.title] - Custom report name (the cut date is appended)
 * @param {string} [options.baseUrl] - App URL for the "open in Tasks" links
 * @returns {{ title: string, contentJson: Object }}
 */
export function buildSummaryReportDoc(report, options = {}) {
    const t = typeof options.t === 'function' ? options.t : (key => key);
    const f = makeFormatters(options.locale || 'en-US');
    const links = makeReportLinks(options.baseUrl);
    const r = report;

    const content = [
        paragraph(text(t('report.summary.meta', {
            date: f.day(r.cutDate),
            projects: r.kpis.projects,
            tasks: r.kpis.totalTasks
        }))),
        ...kpiSection(r, t, f),
        ...highlightsSection(r, t, f),
        ...projectsSection(r, t, f, links),
        ...throughputSection(r, t, f),
        ...deadlinesSection(r, t, f, links),
        ...attentionSection(r, t, f, links),
        ...methodSection(r, t, f)
    ];

    const customTitle = String(options.title || '').trim();
    return {
        title: customTitle
            ? `${customTitle} · ${f.day(r.cutDate)}`
            : t('report.summary.docTitle', { date: f.day(r.cutDate) }),
        contentJson: { type: 'doc', content }
    };
}
